import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useAuth} from '../auth'
import {Product, ACTION_COLORS} from './core/OrderedProductsModel' // Import ACTION_COLORS
import {getOrderedProducts, postProductAction, ActionPayload} from './core/_requests'
import QuantityInputModal from './components/QuantityInputModal' // Assuming this path is correct
import ActionButton from './components/ActionButton' // Assuming this path is correct
import {toast} from 'react-toastify'
import {LoadingState, ErrorState, NoDataState} from '../../components/common/TableStates' // Import common states
import {ImageModal} from '../../components/common/ImageModal' // Import common image modal
import ExportButton from './components/ExportButton' // Import ExportButton component

const OrderedProductsList: React.FC = () => {
  const {formatMessage, locale: language} = useIntl()
  const {currentUser, auth} = useAuth() // auth for token
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // State for the selected status filter. null means 'All' products are shown.
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null) 

  const [clickedActions, setClickedActions] = useState<{[key: string]: {[itemId: number]: boolean}}>({})
  const [showModal, setShowModal] = useState(false)
  const [pendingActionInfo, setPendingActionInfo] = useState<{
    product: Product
    actionType: string
    reasonId: number
  } | null>(null)
  const [showImageModal, setShowImageModal] = useState(false)
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null)

  const fetchProducts = async () => {
    if (!loading) setLoading(true)
    try {
      const response = await getOrderedProducts(currentUser?.token, language)
      setProducts(response.data.data)
      setError(null)
    } catch (error: any) {
      console.error('Error fetching ordered products:', error)
      setError(
        error.response?.data?.message ||
          error.message ||
          formatMessage({id: 'something_wrong_desc'})
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [currentUser, language]) // Added language as dependency

  const submitAction = async (
    product: Product,
    actionType: string,
    reasonId: number,
    availableQuantity?: number
  ) => {
    const token = auth?.token

    // Updated payload structure
    const payload: ActionPayload = {
      item_type: product.item_type, // Kept as it might be needed by the API
      product_id: product.product_id, // Changed from item_id
      quantity: product.quantity, // Added product quantity
      product_from: product.product_from, // Added product_from
      reason_id: reasonId,
    }

    if (product.item_type === 'active' && product.supplier_order_id) { // Check if supplier_order_id exists
      payload.supplier_order_id = product.supplier_order_id // Kept as it might be needed
    }

    // This handles 'available_quantity' for partially available items
    if (availableQuantity !== undefined) {
      payload.available_quantity = availableQuantity
    }

    setClickedActions((prev) => ({
      ...prev,
      [actionType]: {
        ...(prev[actionType] || {}),
        [product.item_id]: true,
      },
    }))

    try {
      await postProductAction(payload, token, language)
      toast.success(formatMessage({id: 'ORDERED_PRODUCTS.ACTION_SUCCESS'}))
      fetchProducts() // Refresh the list
    } catch (error: any) {
      console.error('Error submitting product action:', error)
      toast.error(formatMessage({id: 'ORDERED_PRODUCTS.ACTION_ERROR'}))
      setClickedActions((prev) => ({
        ...prev,
        [actionType]: {
          ...(prev[actionType] || {}),
          [product.item_id]: false,
        },
      }))
    } finally {
      setPendingActionInfo(null)
    }
  }

  const handleActionClick = (product: Product, actionType: string, reasonId: number) => {
    const PARTIALLY_AVAILABLE_REASON_ID = 10 // As per new API response example

    if (reasonId === PARTIALLY_AVAILABLE_REASON_ID) {
      setPendingActionInfo({product, actionType, reasonId})
      setShowModal(true)
    } else {
      submitAction(product, actionType, reasonId)
    }
  }

  const handleModalSubmit = (quantity: number) => {
    if (pendingActionInfo) {
      submitAction(
        pendingActionInfo.product,
        pendingActionInfo.actionType,
        pendingActionInfo.reasonId,
        quantity
      )
    }
    setShowModal(false)
  }

  const handleModalHide = () => {
    setShowModal(false)
    setPendingActionInfo(null)
  }

  const handleImageClick = (imageUrl: string) => {
    setEnlargedImageUrl(imageUrl)
    setShowImageModal(true)
  }

  const handleCloseImageModal = () => {
    setShowImageModal(false)
    setEnlargedImageUrl(null)
  }

  if (loading) {
    return <LoadingState /> // Use common LoadingState
  }

  if (error) {
    return <ErrorState message={error} /> // Use common ErrorState
  }

  // Filter products based on selectedStatus before rendering the table.
  // If selectedStatus is null, all products are displayed.
  // Handle the mapping between filter values and actual API status values.
  const productsToDisplay = selectedStatus
    ? products.filter(product => {
        const statusLower = product.item_status.toLowerCase();
        const selectedLower = selectedStatus.toLowerCase();
        
        // Handle the mapping between filter button values and API status values
        if (selectedLower === 'out of stock') {
          return statusLower === 'out of stock' || product.item_type === 'removed';
        }
        
        return statusLower === selectedLower;
      })
    : products;

  if (!products.length && !loading) { // Show no data only if not loading and products array is empty
    return (
      <div className='card'>
        <div className='card-body py-9 text-center'>{formatMessage({id: 'no_data'})}</div>
      </div>
    )
  }


  return (
    <div className='card'>
      <div className='card-body py-3'>
        {/* Status Filter Section */}
        <div className="my-4 py-3"> {/* Container for the status filter with top/bottom margin and padding */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">{formatMessage({ id: 'STATUS_FILTER.LABEL' })}</h5> {/* Filter section label */}
            <ExportButton products={productsToDisplay} disabled={!productsToDisplay.length} />
          </div>
          <div className="d-flex justify-content-start flex-wrap"> {/* Button group for status filters */}
            <button
              className={`btn btn-sm ${!selectedStatus ? 'btn-dark' : 'btn-outline-dark'} me-2 mb-2`}
              onClick={() => setSelectedStatus(null)}
            >
              {formatMessage({ id: 'ALL' })}
            </button>
            {[
              { key: 'pending', translationKey: 'ORDERED_PRODUCTS.STATUS.PENDING' },
              { key: 'out of stock', translationKey: 'ORDERED_PRODUCTS.STATUS.REMOVED' },
              { key: 'ready', translationKey: 'ORDERED_PRODUCTS.STATUS.READY' }
            ].map(({ key, translationKey }) => (
              <button
                key={key}
                className={`btn btn-sm ${selectedStatus === key ? 'btn-dark' : 'btn-outline-dark'} me-2 mb-2`}
                onClick={() => setSelectedStatus(key)}
              >
                {formatMessage({ id: translationKey })}
              </button>
            ))}
          </div>
        </div>

        {productsToDisplay.length > 0 ? (
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th className='min-w-200px'>{formatMessage({id: 'product'})}</th>
                <th className='min-w-150px'>{formatMessage({id: 'ORDERED_PRODUCTS.BARCODE'})}</th>
                <th className='min-w-150px'>{formatMessage({id: 'ORDERED_PRODUCTS.MODEL'})}</th>
                <th className='min-w-50px text-center'>{formatMessage({id: 'ORDERED_PRODUCTS.QUANTITY'})}</th>
                <th className='min-w-150px text-center'>{formatMessage({id: 'ORDERED_PRODUCTS.STATUS'})}</th>
                <th className='min-w-200px text-start'>{formatMessage({id: 'actions'})}</th>
              </tr>
            </thead>
            <tbody>
              {productsToDisplay.map((product) => (
                <tr key={`${product.product_id}-${product.item_id}`}>
                  <td>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-75px me-5'>
                        <img
                          src={product.image || '/media/stock/ecommerce/1.gif'}
                          alt={product.product_name}
                          onClick={() => handleImageClick(product.image || '/media/stock/ecommerce/1.gif')}
                          style={{cursor: 'pointer'}}
                        />
                      </div>
                      <div className='d-flex justify-content-start flex-column'>
                        <span className='text-dark fw-bolder fs-6'>
                          {product.product_name}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className='text-muted fw-bold text-muted d-block fs-7'>
                      {product.barcode}
                    </span>
                  </td>
                  <td>
                    <span className='text-muted fw-bold text-muted d-block fs-7'>
                      {product.model}
                    </span>
                  </td>
                  <td className='text-center'>
                    <span className='text-dark fw-bolder d-block fs-6'>
                      {product.quantity}
                    </span>
                  </td>
                  <td className='text-center'>
                    <div>
                      <span
                        className='fw-bolder fs-7 d-block mb-1'
                        style={{
                          color: product.status_color,
                        }}
                      >
                        {formatMessage({id: `ORDERED_PRODUCTS.STATUS.${product.item_status.toUpperCase()}`, defaultMessage: product.item_status})}
                      </span>
                      {product.product_from && (() => {
                        let displayValue = product.product_from;
                        const actionKeyStyle = product.product_from!.toUpperCase().replace(/-/g, '_');
                        const potentialTranslationId = `ORDERED_PRODUCTS.ACTIONS.${actionKeyStyle}`;
                        
                        try {
                          const translated = formatMessage({ id: potentialTranslationId });
                          if (translated !== potentialTranslationId) {
                            displayValue = translated;
                          }
                        } catch (e) {
                          // Stick with original displayValue
                        }

                        return (
                          <span className='text-muted fw-bold d-block fs-7'>
                            {formatMessage(
                              {id: 'ORDERED_PRODUCTS.PRODUCT_FROM_DISPLAY', defaultMessage: 'From: {value}'},
                              {value: displayValue}
                            )}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td className='text-start'>
                    <div className='d-flex flex-row justify-content-start flex-wrap'>
                      {Object.entries(product.actions).map(([actionType, reasonId]) => (
                        <div className='me-2 mb-2' key={actionType}>
                          <ActionButton
                            type={actionType as keyof typeof ACTION_COLORS}
                            value={reasonId}
                            isClicked={clickedActions[actionType]?.[product.item_id] || false}
                            onClick={() => handleActionClick(product, actionType, reasonId)}
                          />
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          !loading && <NoDataState /> // Use common NoDataState (or keep specific message if needed)
          // Consider if 'no_data_for_filter' message is better here than generic 'no_data'
          // Keeping specific message for now:
          // !loading && <div className='text-center py-9'>{formatMessage({id: 'no_data_for_filter'})}</div>
        )}
      </div>
      {pendingActionInfo && (
        <QuantityInputModal
          show={showModal}
          orderedQuantity={parseInt(pendingActionInfo.product.quantity, 10)}
          onHide={handleModalHide}
          onSubmit={handleModalSubmit}
        />
      )}

      {/* Use Common Image Modal */}
      <ImageModal
        show={showImageModal}
        imageUrl={enlargedImageUrl}
        onClose={handleCloseImageModal}
        altText={formatMessage({id: 'product'})} // Use appropriate alt text
      />
    </div>
  )
}

export default OrderedProductsList
